import print from 'print-js';
import moment from 'moment';
import { initGrid } from 'utils/grid/grid';

import { onItemListSelection } from 'manage-inventory/actions/manage-inventory.actions';
import { fetchInstantGamesTransitionsHistory } from 'manage-inventory/actions/fetch-instant-games-transitions-history.actions';
import { downloadManageInventoryReportByTab } from 'manage-inventory/actions/download-report-by-tab.actions';

import template from './inventory-transitions-history-chain-tab.html';

const mapStateToThis = ({ session, dictionary, manageInventory, ui, config }) => {
  const { serverTime, locale } = session;
  const { instantGamesTransitionsHistory, isLoading } = manageInventory;
  const { pagination, activeTab } = ui;
  const { pageSizeKey, sortColumnKey, defaultPageSizes, dateFormatPrintables, directionKey, usesPortionedPacks } = config;
  const { packStatuses } = dictionary;
  const terms = dictionary.terms[locale];

  return {
    serverTime,
    locale,
    packStatuses,
    isLoading,
    instantGamesTransitionsHistory,
    pagination,
    pageSizeKey,
    sortColumnKey,
    defaultPageSizes,
    dateFormatPrintables,
    directionKey,
    activeTab,
    terms,
    usesPortionedPacks,
  };
};

const mapDispatchToThis = {
  onItemListSelection,
  fetchInstantGamesTransitionsHistory,
  downloadManageInventoryReportByTab,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$stateParams', 'gettextCatalog', 'tableService', 'Notifications')
class InventoryTransitionsHistoryChainTabController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.print = print;
  }

  $onInit() {
    this.showSubordinates = true;
    this.tableConfig = this.tableService.initConfig({ configName: 'inventoryHistoryChain' });
    this.actions = this.getActions();
    this.headers = this.tableConfig.getHeaders(this.getHeaders());
    this.minDatePicker = moment.unix(this.serverTime).subtract(2, 'years').startOf('year').format(this.dateFormatPrintables);

    this.defaultParams = {
      [this.pageSizeKey]: this.defaultPageSizes[2],
      [this.sortColumnKey]: 'status',
      [this.directionKey]: false,
      retailerId: this.$stateParams.retailerNo,
    };

    this.gridModel = initGrid(this.fetchInstantGamesTransitionsHistory, this.defaultParams);
    this.preserveFilters();
    this.search();
  }

  $onDestroy() {
    this.unsubscribe();
  }

  search() {
    if (this.gamesFilters && this.gamesFilters.$invalid) return;

    this.gridModel.applyFilters().then(() => {
      this.preserveFilters();
    });
  }

  clear() {
    this.gridModel.pagingModel.filters = { subordinateId: this.selectedRetailer.id };
    this.search();
  }

  preserveFilters() {
    this.preservedFilters = { ...this.gridModel.pagingModel.filters };
  }

  onPackSelection(pack) {
    const { retailerId } = this.gridModel.pagingModel.filters;
    const retailerNumber = this.selectedRetailer.displayNumber;
    this.onItemListSelection({ ...pack, retailerId, retailerNumber });
    const hideOrderDetails = pack.orderId === null;

    this.Notifications.showComplexDialog({
      component: 'detailsDialog',
      resolve: {
        hideOrderDetails,
      },
    });
  }

  onDownloadReport() {
    const pagingModel = {
      ...this.gridModel.pagingModel,
      filters: this.preservedFilters,
      pageSize: 0, // BE contract - pageSize = 0 triggers exporting entire data set
    };

    const requestParams = {
      ...pagingModel,
      tabId: this.activeTab,
      retailerId: this.$stateParams.retailerNo,
    };

    return this.downloadManageInventoryReportByTab(requestParams);
  }

  handlePrint() {
    this.print({
      printable: 'inventory-transitions-history-chain-tab',
      type: 'html',
    });
  }

  getActions() {
    return [
      {
        type: 'download',
        function: this.onDownloadReport.bind(this),
        extension: 'xlsx',
      },
      {
        type: 'print',
        function: this.handlePrint.bind(this),
      },
    ];
  }

  getHeaders() {
    return [
      {
        text: this.gettextCatalog.getString('Game No.'),
        field: 'gameNumber',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Game Name'),
        field: 'gameName',
        sortable: true,
      },
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.gettextCatalog.getString('{{ pack }} No.', { pack: this.terms.PACK.singular }),
        field: 'packNo',
        sortable: true,
        textAlignment: 'text-right',
      },
      this.usesPortionedPacks
        ? {
            /// {{ portion }} is Lottery term using to determine Portion.
            text: this.gettextCatalog.getString('{{ portion }} No.', { portion: this.terms.PORTION.singular }),
            field: 'portionNumber',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      this.usesPortionedPacks
        ? {
            text: this.gettextCatalog.getString('Ticket Range'),
            field: 'ticketRange',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      {
        text: this.gettextCatalog.getString('Price Point'),
        field: 'pieceValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.usesPortionedPacks
          ? this.gettextCatalog.getString('{{ pack }}/{{ portion }} Value', {
              pack: this.terms.PACK.singular,
              portion: this.terms.PORTION.singular,
            })
          : this.gettextCatalog.getString('{{ pack }} Value', { pack: this.terms.PACK.singular }),
        field: 'packGrossValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Current Status'),
        field: 'status',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ issued }} is Lottery term using to determine Pack status.
        text: this.gettextCatalog.getString('{{ issued }}', { issued: this.terms.ISSUED.singular }),
        field: 'issuedDate',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ issuedMethod }} is Lottery term using to determine issued method.
        text: this.gettextCatalog.getString('{{ issuedMethod }}', { issuedMethod: this.terms.ISSUED_METHOD.singular }),
        field: 'orderType',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ confirmed }} is Lottery term using to determine Pack status.
        text: this.gettextCatalog.getString('{{ confirmed }}', { confirmed: this.terms.CONFIRMED.singular }),
        field: 'confirmedDate',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ activated }} is Lottery term using to determine Pack status.
        text: this.gettextCatalog.getString('{{ activated }}', { activated: this.terms.ACTIVATED.singular }),
        field: 'activatedDate',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ settled }} is Lottery term using to determine Pack status.
        text: this.gettextCatalog.getString('{{ settled }}', { settled: this.terms.SETTLED.singular }),
        field: 'settledDate',
        sortable: true,
        textAlignment: 'text-right',
      },
    ];
  }

  onRetailerClick(retailer) {
    this.selectedRetailer = retailer;
    this.showSubordinates = false;

    this.gridModel.pagingModel.filters = {
      ...this.gridModel.pagingModel.filters,
      subordinateId: retailer.id,
    };

    this.search();
  }
}

export default class InventoryTransitionsHistoryChainTabComponent {
  constructor() {
    this.controller = InventoryTransitionsHistoryChainTabController;
    this.template = template;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/manage-inventory/components/inventory-transitions-history-chain-tab/inventory-transitions-history-chain-tab.component.js